/**
 * Skill Averages Grid Component
 * Displays a grid of skill average cards showing competency mastery levels
 */

import React from 'react';
import { getStudentSkillAverages } from './scoreUtils';
import { getSkillAverageTextClass, getInterpolatedScoreColor } from './viewUtils';

/**
 * Displays a grid of skill averages for a student
 * @param {Object} props - Component props
 * @param {string} props.studentId - The student's user ID
 * @param {Array} props.tags - Array of all skill tags
 * @param {Array} props.tagFeedbacks - Array of all tag feedbacks
 * @param {boolean} props.showTitle - Whether to show the section title (default: true)
 */
export const SkillAveragesGrid = ({ 
  studentId, 
  tags, 
  tagFeedbacks,
  showTitle = true 
}) => {
  const skillAverages = getStudentSkillAverages(studentId, tags, tagFeedbacks);
  
  // Find the index of the first AD skill
  const firstADIndex = skillAverages.findIndex(({ skill }) => skill.startsWith('AD: '));
  
  return (
    <div className="skill-averages-container">
      {showTitle && (
        <h3 className="transcript-title">
          Competencies Summary
        </h3>
      )}
      
      <div className="skill-averages-grid">
        {skillAverages.map(({ skill, average, count }, index) => {
          const textClasses = getSkillAverageTextClass(average);
          
          // Get interpolated colors for gradient background
          const lightColor = getInterpolatedScoreColor(average, 'light');
          const darkColor = getInterpolatedScoreColor(average, 'dark');
          
          // Build inline style for the card with interpolated gradient
          const cardStyle = average !== null ? {
            background: `linear-gradient(135deg, ${lightColor} 0%, ${darkColor} 100%)`,
            border: `2px solid ${lightColor}`,
            boxShadow: `0 2px 8px ${lightColor}40`
          } : {};
          
          // Add divider if this is the first AD skill
          const shouldShowDivider = firstADIndex !== -1 && index === firstADIndex;
          
          return (
            <React.Fragment key={skill}>
              {shouldShowDivider && (
                <div className="skill-divider"></div>
              )}
              <div 
                className={`skill-average-card ${average === null ? 'skill-average-card-none' : ''}`}
                style={cardStyle}
              >
                <div className={`skill-average-name ${textClasses.split(' ')[0]}`}>
                  {skill}
                </div>
                <div className={`skill-average-score ${textClasses.split(' ')[1]}`}>
                  {average === null ? '—' : `${average}`}
                </div>
                <div className={`skill-average-count ${textClasses.split(' ')[2]}`}>
                  {count === 0 ? 'Not Demonstrated Yet' : `Demonstrated in ${count} project${count === 1 ? '' : 's'}`}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

